"use client";

import { FC, Fragment, HTMLAttributes, ReactNode, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";

import { useDisclosure } from "@mantine/hooks";
import { Modal } from "@mantine/core";

import HtmlText from "../common/HtmlText";
import SectionHeader from "../common/SectionHeader";
import RevealOnScroll from "@/components/common/RevealOnScroll";

import Cross from "@/components/icons/Cross";
import Twitter from "@/components/icons/Twitter";
import Linkedin from "@/components/icons/Linkedin";
import Gmail from "@/components/icons/Gmail";
import Whatsapp from "@/components/icons/Whatsapp";
import Teams from "@/components/icons/Teams";

// -----------------------------------------------------------------------------------------

export type IteamMember = {
  image?: { src: string; alt: string };
  name: string;
  designation?: string;
  achievement?: string;
  about?: string;
  socialHandles?: {
    platform?: "twitter" | "linkedin" | "whatsapp" | "teams" | "gmail";
    link?: string;
  }[];
};

export type ITeamSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  teamMembers?: IteamMember[];
};

const socialHandle = {
  twitter: <Twitter variant="white" />,
  linkedin: <Linkedin variant="white" />,
  whatsapp: <Whatsapp variant="white" />,
  teams: <Teams variant="white" />,
  gmail: <Gmail variant="white" />,
};

const TeamSection: FC<ITeamSectionProps> = (props) => {
  const { heading, description, teamMembers, about, className, ...other } =
    props;

  const [selectedMemberID, setSelectedMemberID] = useState<number>(0);

  const [opened, { open, close }] = useDisclosure(false);

  return (
    <section className={clsx("container py-14 md:py-20 xl:py-24")} {...other}>
      <div className="w-full">
        {(heading || description) && (
          <RevealOnScroll>
            <SectionHeader
              title={heading}
              subtitle={description}
              align="left"
              className="mb-8 md:mb-12"
            />
          </RevealOnScroll>
        )}

        {teamMembers && teamMembers.length > 0 && (
          <div className="grid w-full place-items-center gap-5 md:grid-cols-2 lg:grid-cols-4 lg:gap-7">
            {teamMembers.map((member, idx) => (
              <RevealOnScroll
                key={idx}
                index={idx}
                className="h-full w-full max-w-80 md:max-w-none"
              >
                <div
                  className="group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-2xl shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  onClick={() => {
                    setSelectedMemberID(idx);
                    open();
                  }}
                >
                  <div className="relative flex h-[300px] w-full flex-col overflow-hidden md:h-[330px] lg:h-[228px] xl:h-[285px]">
                    {member.image && (
                      <Image
                        src={member.image.src}
                        alt={member.image.alt}
                        height={320}
                        width={276}
                        className="h-full w-full bg-primary-100 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}

                    {member.achievement && (
                      <span className="absolute bottom-0 w-full bg-yellow-400 px-2 py-1.5 text-center">
                        <p className="text-sm font-medium text-white">
                          {member.achievement}
                        </p>
                      </span>
                    )}
                  </div>

                  <div className="h-auto w-full flex-1 bg-primary-900 p-5">
                    {member.name && (
                      <span className="mb-2 block font-bricolage-grotesque text-h7 font-medium text-white xl:mb-3 xl:text-h5">
                        {member.name}
                      </span>
                    )}

                    {member.designation && (
                      <p className="text-sm font-extralight text-primary-100">
                        {member.designation}
                      </p>
                    )}
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        )}
      </div>

      {teamMembers && teamMembers.length > 0 && (
        <Modal
          opened={opened}
          onClose={close}
          centered
          withCloseButton={false}
          classNames={{
            body: "bg-primary-900 p-0",
            content:
              "max-h-[80vh] max-w-[250px] flex-1 sm:max-w-[280px] md:max-w-[360px] lg:max-w-2xl xl:max-w-4xl",
            inner: "z-[999999]",
            overlay: "z-[999999]",
          }}
        >
          <div className="relative flex w-full flex-col lg:flex-row">
            <div className="relative">
              {teamMembers[selectedMemberID].image?.src && (
                <div className="relative w-full lg:w-[240px] xl:w-[276px]">
                  <Image
                    src={teamMembers[selectedMemberID].image?.src ?? ""}
                    alt={teamMembers[selectedMemberID].image?.alt ?? ""}
                    height={320}
                    width={276}
                    className="h-auto max-h-full w-full bg-primary-200 object-cover"
                  />

                  {teamMembers[selectedMemberID].achievement && (
                    <span className="absolute bottom-0 w-full bg-yellow-400 px-2 py-1 text-center">
                      <p className="text-base font-normal text-white">
                        {teamMembers[selectedMemberID].achievement}
                      </p>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="w-full bg-primary-900 px-4 py-4 md:pt-6 lg:px-7 lg:pt-10 xl:pt-12">
              <div className="mb-4">
                {teamMembers[selectedMemberID].name && (
                  <h4 className="mb-3 text-h6 font-normal text-white lg:mb-5 lg:text-h5 xl:text-h4">
                    {teamMembers[selectedMemberID].name}
                  </h4>
                )}

                {teamMembers[selectedMemberID].designation && (
                  <p className="text-sm font-extralight text-primary-100">
                    {teamMembers[selectedMemberID].designation}
                  </p>
                )}
              </div>

              {teamMembers[selectedMemberID].socialHandles && (
                <div className="flex items-center gap-2.5">
                  {teamMembers[selectedMemberID].socialHandles?.map(
                    (item, idx) => (
                      <Fragment key={idx}>
                        {item.link && item.platform && (
                          <Link href={item.link} target="_blank">
                            {socialHandle[item.platform]}
                          </Link>
                        )}
                      </Fragment>
                    ),
                  )}
                </div>
              )}

              {teamMembers[selectedMemberID].about && (
                <HtmlText className="mt-4 text-sm text-primary-100">
                  {teamMembers[selectedMemberID].about}
                </HtmlText>
              )}

              <div
                className="absolute right-3 top-3 cursor-pointer lg:right-6 lg:top-6"
                onClick={close}
              >
                <Cross className="h-5 w-5 overflow-hidden fill-black-900 align-middle lg:fill-black-200" />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default TeamSection;
